/** Dashboard (merchant/admin UI) languages. Storefront content languages are separate (see stores.languages). */
export const UI_LOCALES = ["en", "fr", "ar"] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

export const UI_LOCALE_NAMES: Record<UiLocale, string> = { en: "English", fr: "Français", ar: "العربية" };

export const UI_LOCALE_COOKIE = "sl_lang";

export function isUiLocale(v: unknown): v is UiLocale {
  return typeof v === "string" && (UI_LOCALES as readonly string[]).includes(v);
}

/** Map any stored locale ("fr-CA", "es", null) to a supported dashboard language. */
export function uiLocaleFrom(v: string | null | undefined): UiLocale {
  const base = (v ?? "").toLowerCase().split(/[-_]/)[0];
  return isUiLocale(base) ? base : "en";
}

export function isRtlLocale(locale: string) {
  return ["ar", "he", "fa", "ur"].includes(locale.toLowerCase().split(/[-_]/)[0]);
}

export const dirOf = (locale: string): "rtl" | "ltr" => (isRtlLocale(locale) ? "rtl" : "ltr");

/** BCP-47 tag for Intl formatting. Arabic uses Latin digits so prices, SKUs and order numbers stay unambiguous. */
export function intlLocale(locale: string): string {
  const base = locale.toLowerCase().split(/[-_]/)[0];
  if (base === "ar") return "ar-u-nu-latn";
  return locale;
}

/** Pick the best supported language from an Accept-Language header. */
export function negotiateUiLocale(acceptLanguage: string | null | undefined): UiLocale {
  for (const part of (acceptLanguage ?? "").split(",")) {
    const tag = part.split(";")[0].trim();
    const base = tag.toLowerCase().split("-")[0];
    if (isUiLocale(base)) return base;
  }
  return "en";
}
